/** @jsx jsx */

import {
  Fragment as _,
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { createPortal } from 'react-dom';
import { css, jsx } from '@emotion/core';
import getBlockById from 'services/apiHelpers/getBlockById';
import { Box } from '@chameleoncreator/creator-ui';
import { useCachedResource } from 'services/useCachedResource';
import { CHAMELEON_CACHE_v1 } from 'services/imageCache';
import { Orbs, getAnimatedOrbsDefaultValues } from './Orbs';

const Background = ({
  id,
  background,
  backgroundImageOpacity,
  backgroundBlur = 0,
}) => {
  let cachedBackgroundImage = useCachedResource(CHAMELEON_CACHE_v1, background);

  return (
    <div
      id={`topic-background-${id}`}
      style={{
        position: 'fixed',
        left: 0,
        top: 0,
        width: '100vw',
        height: '100vh',
        ...(background && {
          background: `url('${cachedBackgroundImage ||
            background}') center / cover no-repeat`,
        }),

        opacity: backgroundImageOpacity / 100,
        filter: `blur(${backgroundBlur}px)`,
        // scale the thing up to not have blurry edges
        ...(backgroundBlur > 0 && {
          transformOrigin: 'center',
          transform: 'scale(1.1)',
        }),
        pointerEvents: 'none',
      }}
    />
  );
};

const BackgroundWithParallax = props => {
  const {
    // state,
    interactions,
    background,
    backgroundImageOpacity,
    backgroundBlur = 0,
    parallaxValue = 10,
    width,
    height,
  } = props;

  const cachedBackgroundImage = useCachedResource(
    CHAMELEON_CACHE_v1,
    background
  );

  const [
    refreshParallaxScrollOnTopicChange,
    setRefreshParallaxScrollOnTopicChange,
  ] = useState<boolean>(false);

  const img = useRef<HTMLImageElement | null>(null);
  const setImageRef = useCallback(node => {
    img.current = node;
  }, []);

  const minHeight = `${100 + parallaxValue}vh`;

  useEffect(() => {
    if (!img.current) {
      return;
    }

    const parallaxThrottler = () => {
      // we can implement whatever more specific throttling approach...
      requestAnimationFrame(parallax);
    };

    const parallax = () => {
      if (!img.current) {
        return;
      }

      const { innerHeight, scrollY } = window;
      const imgTravel = img.current.offsetHeight - innerHeight;
      const scrollTravel = innerHeight * (interactions.length - 1);
      const scrollUnit = imgTravel / scrollTravel;
      const scrollValue = scrollY * scrollUnit;

      if (scrollValue > imgTravel) {
        return;
      }

      //
      img.current.style.transform = `translateY(-${scrollValue}px)`;
    };
    //

    window.addEventListener('scroll', parallaxThrottler);
    window.addEventListener('resize', parallaxThrottler);
    parallax();

    return () => {
      window.removeEventListener('scroll', parallaxThrottler);
      window.removeEventListener('resize', parallaxThrottler);
    };
  }, [setImageRef, refreshParallaxScrollOnTopicChange]);

  useEffect(() => {
    if (!img.current) {
      return;
    }

    let tick;

    if (document.body.scrollHeight > window.innerHeight) {
      const imgHeightOnLoad = img.current.offsetHeight;

      let checkLimit = 0;

      tick = setInterval(() => {
        if (!img.current) {
          return;
        }

        if (img.current.offsetHeight !== imgHeightOnLoad) {
          clearInterval(tick);
          setRefreshParallaxScrollOnTopicChange(true);
        }

        //
        if (checkLimit >= 300) {
          clearInterval(tick);
        }
        checkLimit += 1;
        //
      }, 1);
    }

    return () => {
      clearInterval(tick);
    };
  }, [setImageRef, setRefreshParallaxScrollOnTopicChange]);

  return (
    <_>
      <div
        style={{
          position: 'fixed',
          left: 0,
          top: 0,
          width: '100vw',
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
        <img
          ref={setImageRef}
          src={cachedBackgroundImage || background}
          style={{
            all: 'initial',
            position: 'absolute',
            objectFit: 'cover',
            minWidth: '100vw',
            minHeight,
            opacity: backgroundImageOpacity / 100,
            filter: `blur(${backgroundBlur}px)`,
          }}
        />
      </div>
    </_>
  );
};

const GradientBackground = ({ backgroundGradientData }) => {
  const {
    backgroundPrimaryColor,
    backgroundSecondaryColor,
    backgroundGradientAngle = 0,
  } = backgroundGradientData || {};

  return (
    <div
      css={css({
        position: 'fixed',
        width: '100vw',
        height: '100vh',
        background: `linear-gradient(${backgroundGradientAngle}deg, ${backgroundSecondaryColor}, ${backgroundPrimaryColor})`,
      })}
    />
  );
};

const TopicContainer = props => {
  const { state, theme, topic, children } = props as any;

  const block: any = getBlockById(topic.id);

  let {
    src,
    imageId,
    width,
    height,
    backgroundImage,
    backgroundImageOpacity = 100,
    backgroundBlur = 0,
    parallax = false,
    parallaxValue = 10,
    backgroundGradient,
    backgroundSecondaryColor,
    backgroundGradientAngle = 0,
    backgroundGradientFullHeight = false,
    backgroundStyle,
  }: // primaryOrbColor,
  // secondaryOrbColor,
  // tertiaryOrbColor,
  // orbOpacity = 100,
  // orbBlurStrength = 30,
  // orbSpeed = 2,
  any = block.props;

  const {
    primaryOrbColor,
    secondaryOrbColor,
    tertiaryOrbColor,
    orbOpacity,
    orbBlurStrength,
    orbSpeed,
  } = getAnimatedOrbsDefaultValues(block.props);

  const main = useRef<HTMLDivElement>(null);

  // dirty gotta try and cover for legacy backgroundImage
  if (src) {
    backgroundImage = src;
  }

  let backgroundColor =
    block.props.backgroundColor || theme.backgroundColor || '';

  if (backgroundGradient && backgroundGradientFullHeight) {
    backgroundColor = `linear-gradient(${backgroundGradientAngle}deg, ${backgroundSecondaryColor}, ${block.props.backgroundColor})`;
  }

  // this conditional bit evaluates if there was an image in the legacy format. but backgroundStyle is undefined
  const _backgroundImage_ =
    backgroundStyle === undefined && backgroundImage
      ? backgroundImage
      : backgroundStyle === 'backgroundImage'
      ? backgroundImage
      : undefined;

  // legacy was
  // backgroundStyle === 'backgroundImage' ? backgroundImage : undefined

  return (
    // @ts-ignore
    <Box
      ref={main}
      className="fader_-5s"
      id="main"
      tabindex="-1"
      css={css`
        position: relative;
        background: ${backgroundColor};
        ${backgroundGradient &&
          backgroundGradientFullHeight &&
          `
        background-size: 100vw
          ${`calc(100vh * ${children?.props?.interactions.length})`};
          `}
      `}
    >
      {backgroundGradient && !backgroundGradientFullHeight && (
        <GradientBackground
          backgroundGradientData={{
            backgroundGradientAngle,
            backgroundPrimaryColor: backgroundColor,
            backgroundSecondaryColor,
          }}
        />
      )}

      {window.dev() || !parallax ? (
        <Background
          id={topic.id}
          background={_backgroundImage_}
          backgroundImageOpacity={backgroundImageOpacity}
          backgroundBlur={backgroundBlur}
        />
      ) : (
        <BackgroundWithParallax
          // state={state}
          interactions={children.props.interactions}
          background={_backgroundImage_}
          backgroundImageOpacity={backgroundImageOpacity}
          backgroundBlur={backgroundBlur}
          parallaxValue={parallaxValue}
          width={width}
          height={height}
        />
      )}

      {backgroundStyle === 'animatedOrbs' && (
        <Orbs
          {...{
            orbsFor: 'topic',
            primaryOrbColor,
            secondaryOrbColor,
            tertiaryOrbColor,
            orbOpacity,
            orbBlurStrength,
            orbSpeed,
          }}
        />
      )}

      {children}
    </Box>
  );
};

export default TopicContainer;
